import { useEffect, useRef } from 'react';
import { showToast } from '../../Toast';
import { notifyInboxTargetsGone } from '../../../utils/notificationInbox';
import { missionFocusGate } from './missionFocusGate';
import { chooseRestoredView, viewRestoreGate } from './viewRestore';

const DEFAULT_VIEW_TYPES = ['flow', 'kanban', 'table', 'gantt', 'todolist', 'neural', 'board'];

/**
 * useInitialNavigation Hook
 * 
 * Handles initial navigation state from external sources (e.g., sidebar search, history navigation):
 * - Sets initial view (flow/kanban/table) when navigating to a sprint
 * - Focuses on a specific mission when navigating from search results
 * - Supports custom views via { customViewId, type } format
 * 
 * @param {Object} options
 * @param {string|Object|null} options.initialView - Initial view to set:
 *   - String: 'flow', 'kanban', 'table', 'gantt', 'todolist' for default views
 *   - Object: { customViewId: string, type: string } for custom views
 * @param {string|null} options.initialMissionFocus - Mission ID to focus on
 * @param {Array} options.tasks - Current tasks array (to verify mission exists)
 * @param {string} options.currentView - Current view from toolbar state
 * @param {Function} options.setCurrentView - Function to change view
 * @param {Function} options.onViewChanged - Callback after view is changed
 * @param {Function} options.onMissionFocused - Callback after mission is focused
 * @param {Function} options.onSelectCustomView - Function to select a custom view by ID
 * @param {Function} options.onSelectViewByType - Function to select the first view matching a type
 */
const useInitialNavigation = ({
  initialView,
  initialMissionFocus,
  tasks,
  currentView,
  setCurrentView,
  onViewChanged,
  onMissionFocused,
  onSelectCustomView,
  onSelectViewByType,
  viewsReady = false, // Whether campaign views have been loaded
  views = [], // Views for the campaign currently on screen
  rememberedView = null, // Last view this tab opened on this campaign
  skipUrlUpdate = false, // Whether to skip URL updates (used when restoring from history navigation)
  campaignId = null, // Local campaign currently on screen
  targetCampaignId = null, // Campaign the navigation is opening (may lead local state by a frame)
}) => {
  // Track previous values to detect actual changes (avoids needing currentView in deps)
  // Initialize to null so the first render with a value will trigger the effect
  const prevInitialViewRef = useRef(null);
  const prevInitialMissionFocusRef = useRef(null);
  const hasProcessedInitialViewRef = useRef(false);
  const prevCampaignIdRef = useRef(campaignId);
  // Store currentView in a ref so we can access it without adding to deps
  const currentViewRef = useRef(currentView);
  currentViewRef.current = currentView;
  
  // Track skipUrlUpdate flag - once set true for a navigation, keep it true until next navigation
  // This handles the case where initialView is cleared (null) after processing but we still need
  // to know not to update the URL
  const skipUrlUpdateRef = useRef(false);
  if (skipUrlUpdate) {
    skipUrlUpdateRef.current = true;
  }
  
  // Reset refs when campaign changes - this allows re-focusing the same mission in a new campaign
  // Also reset the skipUrlUpdate ref to false for the next navigation cycle
  if (campaignId !== prevCampaignIdRef.current) {
    console.log('[useInitialNavigation] Campaign changed, resetting refs:', { 
      from: prevCampaignIdRef.current, 
      to: campaignId 
    });
    prevInitialViewRef.current = null;
    prevInitialMissionFocusRef.current = null;
    hasProcessedInitialViewRef.current = false;
    prevCampaignIdRef.current = campaignId;
    // Don't reset skipUrlUpdateRef here - we want to keep it for this navigation cycle
  }

  const viewsKey = (views || []).map((view) => view?.id).filter(Boolean).join(',');
  const viewsRef = useRef(views);
  viewsRef.current = views;

  // Handle initial view from navigation (e.g., from sidebar search, URL load)
  useEffect(() => {
    const gate = viewRestoreGate({
      localCampaignId: campaignId,
      targetCampaignId,
      viewsReady,
    });
    console.log('[useInitialNavigation] Effect:', { initialView, viewsReady, gate, hasProcessed: hasProcessedInitialViewRef.current });

    // The previous campaign can still be on screen for a frame. Picking now
    // misses Board and falls through to Flow, then clears the request.
    if (gate === 'defer') return;

    // Don't process if we've already handled an initialView
    // Also skip if initialView became null after we processed (it was cleared by onViewChanged)
    if (hasProcessedInitialViewRef.current) {
      if (initialView === prevInitialViewRef.current || initialView === null) {
        return;
      }
    }

    const requested = initialView || 'flow';
    const choice = chooseRestoredView({
      remembered: rememberedView,
      requested,
      views: viewsRef.current,
    });
    if (!choice?.id) return;

    hasProcessedInitialViewRef.current = true;
    prevInitialViewRef.current = initialView;
    onSelectCustomView?.(choice.id, skipUrlUpdate);
    onViewChanged?.();
    skipUrlUpdateRef.current = false;
  }, [initialView, viewsReady, viewsKey, rememberedView, skipUrlUpdate, campaignId, targetCampaignId, onSelectCustomView, onViewChanged]);

  // Store pending mission focus that needs to be dispatched after view transition
  const pendingMissionFocusRef = useRef(null);
  // Track if a view transition is currently in progress
  const isViewTransitioningRef = useRef(false);
  
  // Listen for view transition events
  useEffect(() => {
    const handleViewTransitionStart = () => {
      isViewTransitioningRef.current = true;
    };
    
    const handleViewTransitionComplete = (e) => {
      const { view } = e.detail;
      console.log('[useInitialNavigation] View transition complete:', view);
      isViewTransitioningRef.current = false;
      
      // If we have a pending mission focus, dispatch it now
      if (pendingMissionFocusRef.current) {
        const missionId = pendingMissionFocusRef.current;
        pendingMissionFocusRef.current = null;
        
        console.log('[useInitialNavigation] Dispatching pending focusMission for:', missionId, 'in view:', view);
        
        // Small delay to ensure the view's event listener is registered
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('focusMission', { 
            detail: { missionId, view }
          }));
          onMissionFocused?.();
        }, 50);
      }
    };
    
    window.addEventListener('viewTransitionStart', handleViewTransitionStart);
    window.addEventListener('viewTransitionComplete', handleViewTransitionComplete);
    return () => {
      window.removeEventListener('viewTransitionStart', handleViewTransitionStart);
      window.removeEventListener('viewTransitionComplete', handleViewTransitionComplete);
    };
  }, [onMissionFocused]);

  // Track the previous initialView ref to detect view changes
  const prevInitialViewForFocusRef = useRef(null);
  
  // Handle initial mission focus from navigation
  useEffect(() => {
    // Determine if we're navigating to a different view
    const getTargetViewType = () => {
      if (!initialView) return null;
      if (typeof initialView === 'object' && initialView.type) return initialView.type;
      if (typeof initialView === 'string' && DEFAULT_VIEW_TYPES.includes(initialView)) return initialView;
      return null;
    };
    
    const targetViewType = getTargetViewType();
    const isViewChanging = targetViewType && targetViewType !== currentViewRef.current;
    // Also check if a view transition is currently in progress
    const shouldWaitForTransition = isViewChanging || isViewTransitioningRef.current;
    
    const focusGate = missionFocusGate({
      missionId: initialMissionFocus,
      tasks,
      localCampaignId: campaignId,
      targetCampaignId,
    });

    console.log('[useInitialNavigation] Mission focus effect:', {
      initialMissionFocus,
      prevValue: prevInitialMissionFocusRef.current,
      tasksLength: tasks.length,
      focusGate,
      localCampaignId: campaignId,
      targetCampaignId,
      currentView: currentViewRef.current,
      targetViewType,
      isViewChanging,
      isViewTransitioning: isViewTransitioningRef.current,
      shouldWaitForTransition,
    });

    // Don't consume the focus while the previous campaign is still on screen.
    if (focusGate === 'defer') {
      console.log('[useInitialNavigation] Deferring mission focus until campaign switch finishes');
      return;
    }
    
    // Only act if initialMissionFocus actually changed
    if (initialMissionFocus === prevInitialMissionFocusRef.current) {
      console.log('[useInitialNavigation] Mission focus unchanged, skipping');
      return;
    }
    prevInitialMissionFocusRef.current = initialMissionFocus;
    prevInitialViewForFocusRef.current = initialView;
    
    if (focusGate === 'found' || focusGate === 'missing') {
      console.log('[useInitialNavigation] Mission exists:', focusGate === 'found');
      if (focusGate === 'found') {
        if (shouldWaitForTransition) {
          // View is changing or transitioning - store mission and wait for viewTransitionComplete
          pendingMissionFocusRef.current = initialMissionFocus;
          console.log('[useInitialNavigation] View changing/transitioning, stored pending mission focus, waiting for view transition');
        } else {
          // Same view and not transitioning - dispatch focusMission immediately (view is already mounted)
          const view = currentViewRef.current;
          console.log('[useInitialNavigation] Same view, dispatching focusMission immediately:', view, 'mission:', initialMissionFocus);
          
          // Small delay to ensure any React state updates have settled
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('focusMission', { 
              detail: { missionId: initialMissionFocus, view }
            }));
            onMissionFocused?.();
          }, 50);
        }
      } else {
        showToast('This mission no longer exists', { type: 'info' });
        if (campaignId) {
          notifyInboxTargetsGone({ campaignId, missionIds: [initialMissionFocus] });
        }
        onMissionFocused?.();
      }
    }
  }, [initialMissionFocus, initialView, tasks, onMissionFocused, campaignId, targetCampaignId]);
};

export default useInitialNavigation;

